import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App';
import ErrorFallback from './components/ErrorFallback';
import './index.css';

// Initialize Sentry before React mounts. Empty DSN → Sentry is a no-op,
// which is what we want in dev builds without VITE_SENTRY_DSN set.
//
// {@code VITE_SENTRY_RELEASE} is set in CI to the commit SHA (see
// .github/workflows/ci.yml). The same SHA is used by the
// {@code @sentry/cli sourcemaps upload} step, so Sentry can match a
// stack trace from a deployed bundle back to the original {@code .tsx}
// source maps that were uploaded under the same release. Falls back to
// {@code "development"} for local builds where the env var isn't set —
// Sentry treats that as an untagged release, which is fine when there's
// no source map to match against anyway.
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE || 'development',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event) {
      return event;
    },
  });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
